import styles from './PersonalContactsDialog.module.scss';

import {
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from '@work4all/components';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { useUser } from '@work4all/data';

import { User } from '@work4all/models/lib/Classes/User.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useCustomer } from '../../containers/files/detail/components/queries/use-customer';
import { useSupplier } from '../../containers/files/detail/components/queries/use-supplier';
import { UserPickerField } from '../entity-picker/UserPickerField';

import { usePersonalContactsApi } from './use-personal-contacts-api';

interface PersonalContactsDialogProps {
  open: boolean;
  onClose: () => void;
  entity: Entities;
  sdObjMemberId: number;
}

export const PersonalContactsDialog = (props: PersonalContactsDialogProps) => {
  const { open, onClose, entity, sdObjMemberId } = props;

  const { t } = useTranslation();

  const customerRes = useCustomer(sdObjMemberId?.toString());
  const supplierRes = useSupplier(sdObjMemberId?.toString());

  const businessPartnerRes =
    entity === Entities.customer ? customerRes : supplierRes;
  const businessPartner =
    entity === Entities.customer ? customerRes.customer : supplierRes.supplier;

  const user = useUser();
  const [selectedUser, setSelectedUser] = useState<User>({
    id: user.benutzerCode,
    displayName: user.displayName,
  });

  const isPersonalContact = useMemo(() => {
    return businessPartner?.personalContactMappings?.some(
      (x) => x.userId === selectedUser.id
    );
  }, [businessPartner?.personalContactMappings, selectedUser.id]);

  const { togglePersonalContact } = usePersonalContactsApi({
    entity,
  });

  const [search, setSearch] = useState('');

  const handleUserChange = useCallback(
    async (user: User) => {
      setSelectedUser(user);
      await businessPartnerRes.refetch();
    },
    [businessPartnerRes]
  );

  const [filterLayedOff, setFilterLayedOff] = useState(true);

  const sortedContactList = useMemo(() => {
    if (!businessPartner?.contactList) return [];

    let result = [...businessPartner.contactList]
      ?.sort((a, b) => a.displayName.localeCompare(b.displayName))
      ?.sort((a) => {
        return a.id === businessPartner.mainContactId ? -1 : 1;
      })
      ?.map((contact) => ({
        checked: contact.personalContactMappings.some(
          (x) => x.userId === selectedUser.id
        ),
        contact,
      }));

    if (filterLayedOff) {
      result = result?.filter((x) => !x.contact.layedOff);
    }

    if (search) {
      result = result?.filter((x) =>
        x.contact.displayName.toLowerCase().includes(search.toLowerCase())
      );
    }

    return result;
  }, [
    businessPartner?.contactList,
    businessPartner?.mainContactId,
    search,
    selectedUser?.id,
    filterLayedOff,
  ]);

  const handleTogglePersonalContact = useCallback(
    async (state: boolean, contactId?: number) => {
      await togglePersonalContact({
        userId: selectedUser.id,
        sdObjMemberId,
        contactId,
        state,
      });
      businessPartnerRes.refetch();
    },
    [togglePersonalContact, selectedUser.id, sdObjMemberId, businessPartnerRes]
  );

  return (
    <Dialog open={open} onClose={onClose} title={t('PRIVATE_CONTACTS.TITLE')}>
      <Stack p="0 1rem" gap="1rem" width="25rem" maxWidth="100%">
        <UserPickerField
          value={selectedUser}
          onChange={handleUserChange}
          clearable={false}
        />

        {businessPartner?.isPrivateCustomer && (
          <CheckboxRadioItem
            label={t('PRIVATE_CONTACTS.MARK', { name: businessPartner?.name })}
            checked={isPersonalContact}
            onChange={(e) => handleTogglePersonalContact(e.target.checked)}
          />
        )}

        {!businessPartner?.isPrivateCustomer && (
          <>
            <Typography>{t('PRIVATE_CONTACTS.DESCRIPTION')}</Typography>
            <Divider title={t('COMMON.CONTACT', { count: 2 })} />
            <Stack direction="row" gap="1rem">
              <LabeledInput
                label={t('PICKER.SEARCH.DEFAULT')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <CheckboxRadioItem
                label={t('COMMON.EXTINCT', { count: 2 })}
                checked={!filterLayedOff}
                onChange={() => setFilterLayedOff(!filterLayedOff)}
              />
            </Stack>

            <List>
              {sortedContactList.map((mapping, idx) => {
                return (
                  <ListItemButton key={idx} className={styles.listItem}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={mapping.checked}
                        tabIndex={-1}
                        disableRipple
                        style={{ padding: '0.25rem' }}
                        onChange={(e) =>
                          handleTogglePersonalContact(
                            e.target.checked,
                            mapping.contact.id
                          )
                        }
                      />
                    </ListItemIcon>
                    <ListItemText>
                      <div className={clsx(styles.listItemText)}>
                        <Typography
                          variant="body1"
                          className={clsx({
                            [styles.mainContact]:
                              mapping.contact.id ===
                              businessPartner?.mainContactId,
                            [styles.layedOff]: mapping.contact.layedOff,
                          })}
                        >
                          {mapping.contact.displayName}
                        </Typography>
                        {mapping.contact.department?.name && (
                          <Typography
                            variant="body2"
                            color="var(--text03)"
                            className={clsx({
                              [styles.layedOff]: mapping.contact.layedOff,
                            })}
                          >
                            | {mapping.contact.department?.name}
                          </Typography>
                        )}
                      </div>
                    </ListItemText>
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </Stack>
    </Dialog>
  );
};
